import React, { use, useEffect, useState } from "react";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import { Badge } from "../../../Component/AdminComponents/Badge";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { ArrowLeft, BadgeDollarSign, Eye, Pencil, Plus, Power, ShieldCheck, Upload } from "lucide-react";
import BlueButton from "../../../Component/Common/BlueButton";
import ProductModal from "./ProductModal";
import { BASE_URL } from "../../../config/api";
import PriceTemplateModal from "../Admin/PriceTemplateModal";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AccessBlockedModal from "../Admin/AccessBlockedModal";
import AddButton from "../../../Component/Common/AddButton";
import SearchInput from "../../../Component/Common/SearchInput";
import SimpleTable from "../../../Component/Common/SimpleTable";
import UpdateButton from "../../../Component/Common/UpdateButton";
import StatusButton from "../../../Component/Common/StatusButton";
import ProductViewModal from "./ProductViewModal";

export default function ProductView() {
  const navigate = useNavigate();
  const authUserString = localStorage.getItem("authUserData");
  const authUser = authUserString ? JSON.parse(authUserString) : null;

  const userRole = Number(authUser?.role); // 1 = admin, 2 = company
  const companyId = authUser?.id;  // company id for role 2

  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState({})

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [templateProduct, setTemplateProduct] = useState({
    emp_payout: 0,
    retailer_payout: 0,
    other_payout: 0,
    company_payout: 0,
  });

  const [categoryFilter, setCategoryFilter] = useState("All");

    const allCategories = Array.from(
    new Set(
      products.flatMap((p) =>
        p.categories?.map((c) => c.name)
      )
    )
  );



  // ----------------- FETCH API -----------------
  const fetchProducts = async () => {
  try {
    setLoading(true);
    setError("");

    let url = "";

    if (userRole === 1) {
      // ADMIN
      url = `${BASE_URL}warranty/products-with-categories`;
    } else if (userRole === 2) {
      // COMPANY
      url = `${BASE_URL}warranty/company-products?company_id=${companyId}`;
    }

    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to fetch products");

    const data = await res.json();

    let productList = [];
    let product = {};

    if (userRole === 1) {
      // admin response
      productList = data.products || [];
      
    }

   if (userRole === 2) {
  productList =
    data.data?.[0]?.products?.map((item) => ({
      

      // 🔹 full product object (spread)
      ...item.product,
      // 🔹 company-product level
      company_product_id: item.company_product_id,
      company_id: item.company_id,
      margin: item.margin,
      p_status: item.p_status,

      

      // 🔹 keep original objects if needed later
      _company_product: item,
      _company: data.data?.[0]?.company,

    })) || [];

    product = data.data?.[0]?.products[0]?.product || [];
}


    setProducts(productList);
    setProduct(product)
    setFiltered(productList);

  } catch (err) {
    setError(err.message || "Something went wrong");
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchProducts();
}, []);

// ----------------- SEARCH FILTER -----------------

  useEffect(() => {
  let result = products;

  // 🔹 Category filter
  if (categoryFilter !== "All") {
    result = result.filter((p) =>
      p.categories?.some((c) => c.name === categoryFilter)
    );
  }

  // 🔹 Search filter
  result = result.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );

  setFiltered(result);
}, [search, products, categoryFilter]);


  
  // useEffect(() => {
  //   const result = products.filter((p) =>
  //     p.name?.toLowerCase().includes(search.toLowerCase())
  //   );
  //   setFiltered(result);
  // }, [search, products]);

  // ----------------- SUBMIT -----------------
  const handleSubmit = () => {
    setIsOpen(false);
    setSelectedProduct(null);
    fetchProducts();
  };

  // ----------------- ADD / UPDATE -----------------
  const handleAdd = () => {
    setSelectedProduct(null);
    setIsOpen(true);
  };

  const handleUpdate = (product) => {
    setSelectedProduct(product);
    setIsOpen(true);
  };

  // ----------------- TOGGLE STATUS -----------------
  const toggleStatus = async (product) => {
    try {
      const res = await fetch(
        `${BASE_URL}warranty/product/toggle-status/${product.id}`,
        { method: "POST" }
      );

      if (!res.ok) throw new Error("Failed to toggle status");
      fetchProducts();
    } catch (err) {
      alert(err.message || "Something went wrong");
    }
  };

  // ----------------- IMAGE -----------------
  const getImageUrl = (p) => {
    if (p.image) return p.image;
    if (p.categories?.length > 0 && p.categories[0].image)
      return p.categories[0].image;

    return "/placeholder.png";
  };


  const saveTemplate = async () => {
  // ---------- BASIC VALIDATION ----------
  if (!templateProduct?.minValue || !templateProduct?.maxValue) {
    toast.error("Please enter both Min and Max price");
    return;
  }

  if (templateProduct.minValue >= templateProduct.maxValue) {
    toast.error("Max price must be greater than Min price");
    return;
  }

  // 🔒 Max should not exceed product MRP
  if (templateProduct.maxValue > templateProduct.max_value) {
    toast.error(
      `Max price cannot exceed ₹${templateProduct.max_value}`
    );
    return;
  }


  // ---------- PAYOUT VALIDATION ----------
  const totalPayout =
    Number(templateProduct.emp_payout || 0) +
    Number(templateProduct.retailer_payout || 0) +
    Number(templateProduct.other_payout || 0);

  if (totalPayout > templateProduct.maxValue) {
    toast.error("Total payout cannot exceed Max price");
    return;
  }

  const payload = {
    company_id: companyId,
    warranty_product_id: templateProduct.id,
    product_type: templateProduct.product_type,

    min_price: Number(templateProduct.minValue),
    max_price: Number(templateProduct.maxValue),
    product_price: Number(templateProduct.maxValue),

    is_fixed: templateProduct.is_fixed,
    
    fixed_amount: templateProduct.mrp,

    is_percent: templateProduct.is_percent,
    percent: templateProduct.mrp,

    emp_payout: Number(templateProduct.emp_payout || 0),
    retailer_payout: Number(templateProduct.retailer_payout || 0),
    other_payout: Number(templateProduct.other_payout || 0),
    company_payout: Number(templateProduct.company_payout || 0),

    is_premium: 1,
  };

  const toastId = toast.loading("Saving price template...");

  try {
    const res = await fetch(
      `${BASE_URL}warranty/price-templates`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    const data = await res.json();

    if (!res.ok || !data?.status) {
      throw new Error(data?.message || "Failed to save template");
    }

    toast.success("Price template saved successfully!", { id: toastId });

    setIsTemplateOpen(false);

  } catch (err) {
    console.error(err);
    toast.error(err.message || "Something went wrong", { id: toastId });
  }
};

const productRows = filtered.map((p) => {
  const isActive = p.status === 1;

  return {
    raw: p,

    cells: [
      // Image
      {
        title: (
          <div className="flex justify-center">
            <img
              src={getImageUrl(p)}
              alt={p.name}
              className="h-10 w-10 rounded-lg object-cover border"
            />
          </div>
        ),
      },

      // Name
      {
        title: p.name,
      },

      // Categories
      {
        title:
          p.categories?.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-1">
              {p.categories.map((c) => (
                <Badge key={c.id} color="blue">
                  {c.name}
                </Badge>
              ))}
            </div>
          ) : (
            "—"
          ),
      },

      // Validity
      {
        title: p.validity || "—",
      },

      // Claims
      {
        title: p.claims || "—",
      },

      // MRP / Percent
      {
        title: (
          <span className="font-semibold">
            {p.is_fixed === 1 && "₹"}
            {p.mrp}
            {p.is_percent === 1 && "%"}
          </span>
        ),
      },

      // Status
      {
        title: (
          <Badge color={isActive ? "green" : "red"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        ),
      },
    ],
  };
});


  return (
    <div className="space-y-4">
      <SectionCard title="Warranty Products">

        {/* HEADER BUTTONS */}
        <div className="flex flex-wrap justify-between items-center mb-3 gap-4">
        {/* CATEGORY FILTER BADGES */}
        <div className="flex flex-wrap gap-2">
          <span
            onClick={() => setCategoryFilter("All")}
            className={`px-3 py-1.5 text-xs rounded-full cursor-pointer border 
              ${
                categoryFilter === "All"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
          >
            All
          </span>

          {allCategories.map((cat, idx) => (
            <span
              key={idx}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 text-xs rounded-full cursor-pointer border 
                ${
                  categoryFilter === cat
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                }`}
            >
              {cat}
            </span>
          ))}
        </div>

        {/* RIGHT SECTION: Search + Add Product + Refresh */}
        <div className="flex justify-end mb-3 gap-2">

          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
            placeholder="Search product..."
            size="sm"
          />
          {/* ADD PRODUCT — only admin */}
          {userRole === 1 && (
            <AddButton onClick={handleAdd}/>
          )}
          <div className="hidden lg:block">
            <RefreshButton onClick={fetchProducts} />
          </div>
        </div>
      </div>


        {/* MODAL */}
        <ProductModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setSelectedProduct(null);
          }}
          onSubmit={handleSubmit}
          initialData={selectedProduct}
        />

        {/* LOADER */}
        {loading && <RegularLoader />}

        {/* ERROR */}
        {error && (
          <div className="text-center py-4 text-red-500 text-sm">{error}</div>
        )}

        {/* TABLE */}
        {!loading && !error && (

          <>

          <div className="md:hidden space-y-6 px-2">
            {filtered.map((p) => {
              const isActive = p.status === 1;

              return (
                <div
                  key={p.id}
                  className="bg-white rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.12)] overflow-hidden"
                >
                  {/* IMAGE SECTION */}
                  <div className="relative">
                    <img
                      src={getImageUrl(p)}
                      alt={p.name}
                      className="h-44 w-full object-contain p-4 bg-slate-50"
                    />

                    {/* Bookmark Icon */}
                    <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-md p-2 rounded-full shadow-sm">
                       <Badge color={isActive ? "green" : "red"}>
                        {isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>

                  {/* CONTENT */}
                  <div className="p-5">
                    {/* Name + Status */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {p.name}
                      </h3>

                     
                    </div>

                    {/* CATEGORY BADGES */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.categories?.length > 0 ? (
                        p.categories.map((c) => (
                          <span
                            key={c.id}
                            className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium border border-blue-100"
                          >
                            {c.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400">
                          No categories
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    {/* <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                      {p.description ||
                        "Warranty protection with reliable coverage and smart benefits."}
                    </p> */}

                    {/* STATS */}
                    <div className="mt-4 grid grid-cols-3 text-center">
                      <div>
                        <div className="font-semibold text-slate-900">
                          {p.validity || "—"}
                        </div>
                        <div className="text-xs text-slate-400">Validity</div>
                      </div>

                      <div>
                        <div className="font-semibold text-slate-900">
                          {p.claims || "—"}
                        </div>
                        <div className="text-xs text-slate-400">Claims</div>
                      </div>

                      <div>
                        <div className="font-semibold text-slate-900">
                          {p.is_fixed === 1 && "₹"}
                          {p.mrp}
                          {p.is_percent === 1 && "%"}
                        </div>
                        <div className="text-xs text-slate-400">Price</div>
                      </div>
                    </div>

                    {/* ACTION BUTTONS */}
                    <div className="mt-5">
                        {/* ADMIN ACTIONS */}
                        {userRole === 1 && (
                          <div className="grid grid-cols-3 gap-2">

                            {/* View */}
                            <button
                              onClick={() => {
                                setViewProduct(p);
                                setIsViewOpen(true);
                              }}
                              className="flex items-center justify-center gap-1 bg-blue-100 text-blue-700 py-2 rounded-xl text-xs font-medium hover:bg-blue-200 transition"
                            >
                              <Eye size={14} />
                              View
                            </button>

                            {/* Update */}
                            <button
                              onClick={() => handleUpdate(p)}
                              className="flex items-center justify-center gap-1 bg-slate-100 text-slate-800 py-2 rounded-xl text-xs font-medium hover:bg-slate-200 transition"
                            >
                              <Pencil size={14} />
                              Update
                            </button>

                            {/* Activate / Deactivate */}
                            <button
                              onClick={() => toggleStatus(p)}
                              className={`flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-medium transition ${
                                isActive
                                  ? "bg-rose-100 text-rose-600 hover:bg-rose-200"
                                  : "bg-emerald-100 text-emerald-600 hover:bg-emerald-200"
                              }`}
                            >
                              <Power size={14} />
                              {isActive ? "Inactive" : "Active"}
                            </button>

                          </div>
                        )}

                        {/* COMPANY ACTIONS */}
                        {userRole === 2 && (
                          <div className="grid grid-cols-2 gap-2">

                            {/* View */}
                            <button
                              onClick={() => {
                                setViewProduct(p);
                                setIsViewOpen(true);
                              }}
                              className="flex items-center justify-center gap-1 bg-blue-700 text-white py-2 rounded-xl text-xs font-medium hover:bg-blue-800 transition"
                            >
                              <Eye size={14} />
                              View
                            </button>

                            {/* Price Template */}
                            <button
                              onClick={() => {
                                setTemplateProduct({
                                  ...p,
                                  minValue: "",
                                  maxValue: "",
                                  max_value: Number(p.max_value),
                                  min_value: Number(p.min_value),
                                });
                                setIsTemplateOpen(true);
                              }}
                              className="flex items-center justify-center gap-1 bg-indigo-100 text-indigo-700 py-2 rounded-xl text-xs font-medium hover:bg-indigo-200 transition"
                            >
                              <BadgeDollarSign size={14} />
                              Template
                            </button>

                          </div>
                        )}
                      </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden md:block">
            <SimpleTable
              headers={[
                "Image",
                "Name",
                "Categories",
                "Validity",
                "Claims",
                "MRP / %",
                "Status",
              ]}
              rows={productRows}
              renderActions={(p) => {
                const isActive = p.status === 1;

                return (
                  <div className="flex items-center justify-center gap-2">

                    <BlueButton
                      onClick={() => {
                        setViewProduct(p);
                        setIsViewOpen(true);
                      }}
                    >
                      View
                    </BlueButton>

                    {/* ADMIN ACTIONS */}
                    {userRole === 1 && (
                      <>
                        <UpdateButton onClick={() => handleUpdate(p)} />

                        <StatusButton
                          label={isActive ? "Inactive" : "Active"}
                          onClick={() => toggleStatus(p)}
                        />
                      </>
                    )}

                    {/* COMPANY ACTIONS */}
                    {userRole === 2 && (
                      <>
                        <BlueButton
                          className="flex items-center gap-1"
                          onClick={() => {
                            setTemplateProduct({
                              ...p,
                              minValue: "",
                              maxValue: "",
                              max_value: Number(p.max_value),
                              min_value: Number(p.min_value),
                            });
                            setIsTemplateOpen(true);
                          }}
                        >
                          <Plus size={14} /> Price Template
                        </BlueButton>
                      </>
                    )}
                    
                  </div>
                );
              }}
              showSrNo
              pageSize={10}
            />
          </div>
        </>
        )}

        <PriceTemplateModal
          isOpen={isTemplateOpen}
          onClose={() => setIsTemplateOpen(false)}
          products={products}
          product={templateProduct}
          setProduct={setTemplateProduct}
          onSave={saveTemplate}
          errors={{}}   // optional, keep empty for now
        />

      </SectionCard>

      <AccessBlockedModal
        user={authUser}
        allowedRoles={[2]}
        title="Account Details Required"
        message="Account Details is mandatory to use this feature."
        subMessage="Complete Zoho setup from Account → Zoho Details."
      />

      <ProductViewModal
        isOpen={isViewOpen}
        onClose={() => {
          setIsViewOpen(false);
          setViewProduct(null);
        }}
        product={viewProduct}
      />

    </div>
  );
}


// import React, { useEffect, useState } from "react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { Plus, ShieldCheck, Upload } from "lucide-react";
// import BlueButton from "../../../Component/Common/BlueButton";
// import CategoryModal from "./CategoryModal";
// import ProductModal from "./ProductModal";
// import { BASE_URL } from "../../../config/api";

// export default function ProductView() {
//   const [products, setProducts] = useState([]);
//   const [filtered, setFiltered] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [search, setSearch] = useState("");
//   const [isOpen, setIsOpen] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);

//   // ----------------- FETCH API -----------------
//   const fetchProducts = async () => {
//     try {
//       setLoading(true);
//       setError("");

//       const res = await fetch(
//         `${BASE_URL}warranty/products-with-categories`
//       );
//       if (!res.ok) throw new Error("Failed to fetch products");

//       const data = await res.json();
//       setProducts(data.products || []);
//       setFiltered(data.products || []);
//     } catch (err) {
//       setError(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchProducts();
//   }, []);

//   // ----------------- SEARCH FILTER -----------------
//   useEffect(() => {
//     const result = products.filter((p) =>
//       p.name.toLowerCase().includes(search.toLowerCase())
//     );
//     setFiltered(result);
//   }, [search, products]);

//   // ----------------- HANDLE SUBMIT -----------------
//   const handleSubmit = () => {
//     setIsOpen(false);
//     setSelectedProduct(null);
//     fetchProducts();
//   };

//   // ----------------- HANDLE ADD / UPDATE -----------------
//   const handleAdd = () => {
//     setSelectedProduct(null);
//     setIsOpen(true);
//   };

//   const handleUpdate = (product) => {
//     setSelectedProduct(product);
//     setIsOpen(true);
//   };

//   // ----------------- TOGGLE STATUS -----------------
//   const toggleStatus = async (product) => {
//     try {
//       const res = await fetch(
//         `${BASE_URL}warranty/product/toggle-status/${product.id}`,
//         { method: "POST" }
//       );

//       if (!res.ok) throw new Error("Failed to toggle status");
//       fetchProducts();
//     } catch (err) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   // ----------------- GET IMAGE URL (product → category → fallback) -----------------
//   const getImageUrl = (p) => {
//     if (p.image) return p.image;
//     if (p.categories?.length > 0 && p.categories[0].image)
//       return p.categories[0].image;

//     return "/placeholder.png";
//   };

//   // ----------------- UI -----------------
//   return (
//     <div className="space-y-4">
//       <SectionCard title="Warranty Products">

//         {/* HEADER BUTTONS */}
//         <div className="flex flex-wrap justify-end mb-3 gap-2">
//           <input
//             type="text"
//             placeholder="Search product..."
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             className="border text-xs px-3 py-1.5 rounded-lg w-40 bg-slate-50"
//           />

//           <BlueButton className="flex items-center gap-1" onClick={handleAdd}>
//             <Plus size={14} /> Add Product
//           </BlueButton>

//           <RefreshButton onClick={fetchProducts} />
//         </div>

//         <ProductModal
//         isOpen={isOpen}
//         onClose={() => {
//             setIsOpen(false);
//             setSelectedProduct(null);
//         }}
//         onSubmit={handleSubmit}
//         initialData={selectedProduct}
//         />


//         {/* LOADER */}
//         {loading && <RegularLoader />}

//         {/* ERROR */}
//         {error && (
//           <div className="text-center py-4 text-red-500 text-sm">{error}</div>
//         )}

//         {/* TABLE */}
//         {!loading && !error && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-sm text-center border border-slate-100 rounded-lg">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500 bg-slate-50">
//                   <th className="py-2">Image</th>
//                   <th className="py-2">Name</th>
//                   <th className="py-2">Categories</th>
//                   <th className="py-2">Validity</th>
//                   <th className="py-2">Claims</th>
//                   <th className="py-2">MRP</th>
//                   <th className="py-2">Status</th>
//                   <th className="py-2">Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filtered.map((p) => {
//                   const isActive = p.status === 1;

//                   return (
//                     <tr
//                       key={p.id}
//                       className="border-b border-slate-100 bg-white hover:bg-blue-50/60"
//                     >
//                       {/* FIXED IMAGE */}
//                       <td className="py-2">
//                         <img
//                           src={getImageUrl(p)}
//                           alt={p.name}
//                           className="h-10 w-10 rounded-lg object-cover border"
//                         />
//                       </td>

//                       <td className="py-2 text-slate-700 font-semibold">
//                         {p.name}
//                       </td>

//                       <td className="py-2">
//                         <div className="flex flex-wrap justify-center gap-1">
//                           {p.categories.map((c) => (
//                             <Badge key={c.id} color="blue">
//                               {c.name}
//                             </Badge>
//                           ))}
//                         </div>
//                       </td>

//                       <td className="py-2">{p.validity || "—"}</td>
//                       <td className="py-2">{p.claims || "—"}</td>
//                       <td className="py-2 font-semibold">₹{p.mrp}</td>

//                       <td className="py-2">
//                         <Badge color={isActive ? "green" : "red"}>
//                           {isActive ? "Active" : "Inactive"}
//                         </Badge>
//                       </td>

//                       <td className="py-1.5 text-xs">
//                         <div className="flex items-center justify-center gap-2">

//                           {/* Update */}
//                           <button
//                             onClick={() => handleUpdate(p)}
//                             className="text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center gap-1"
//                           >
//                             <Upload className="w-3 h-3" />
//                             <span>Update</span>
//                           </button>

//                           {/* Toggle Status */}
//                           <button
//                             type="button"
//                             className="text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 flex items-center gap-1"
//                             onClick={() => toggleStatus(p)}
//                           >
//                             <ShieldCheck className="w-3 h-3" />
//                             <span>{isActive ? "Inactive" : "Active"}</span>
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>

//             {filtered.length === 0 && (
//               <p className="text-center text-xs text-slate-500 py-4">
//                 No products found.
//               </p>
//             )}
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }


// ***************************************** 25 NOV 2025 ORIGINAL CODE ****************************************************************

// import React, { useEffect, useState } from "react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { format } from "date-fns";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { Plus, ShieldCheck, Upload } from "lucide-react";
// import BlueButton from "../../../Component/Common/BlueButton";
// import CategoryModal from "./CategoryModal";

// export default function ProductView() {
//   const [products, setProducts] = useState([]);
//   const [filtered, setFiltered] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [search, setSearch] = useState("");
//   const [isOpen, setIsOpen] = useState(false);
//   const [selectedProduct, setSelectedProduct] = useState(null);

//   // ----------------- FETCH API -----------------
//   const fetchProducts = async () => {
//     try {
//       setLoading(true);
//       setError("");

//       const res = await fetch(
//         "https://goelectronix.in/api/v1/warranty/products-with-categories"
//       );
//       if (!res.ok) throw new Error("Failed to fetch products");

//       const data = await res.json();
//       setProducts(data.products || []);
//       setFiltered(data.products || []);
//     } catch (err) {
//       setError(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchProducts();
//   }, []);

//   // ----------------- SEARCH FILTER -----------------
//   useEffect(() => {
//     const result = products.filter((p) =>
//       p.name.toLowerCase().includes(search.toLowerCase())
//     );
//     setFiltered(result);
//   }, [search, products]);

//   // ----------------- HANDLE SUBMIT -----------------
//   const handleSubmit = () => {
//     setIsOpen(false);
//     setSelectedProduct(null);
//     fetchProducts();  
//   };

//   // ----------------- HANDLE ADD / UPDATE -----------------
//   const handleAdd = () => {
//     setSelectedProduct(null);
//     setIsOpen(true);
//   };

//   const handleUpdate = (product) => {
//     setSelectedProduct(product);
//     setIsOpen(true);
//   };

//   // ----------------- TOGGLE STATUS -----------------
//   const toggleStatus = async (product) => {
//     try {
//       const res = await fetch(
//         `https://goelectronix.in/api/v1/warranty/product/toggle-status/${product.id}`,
//         { method: "POST" }
//       );

//       if (!res.ok) throw new Error("Failed to toggle status");
//       fetchProducts();
//     } catch (err) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   // ----------------- UI -----------------
//   return (
//     <div className="space-y-4">
//       <SectionCard title="Warranty Products">
        
//         {/* HEADER BUTTONS */}
//         <div className="flex flex-wrap justify-end mb-3 gap-2">
//           <input
//             type="text"
//             placeholder="Search product..."
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//             className="border text-xs px-3 py-1.5 rounded-lg w-40 bg-slate-50"
//           />

//           <BlueButton className="flex items-center gap-1" onClick={handleAdd}>
//             <Plus size={14} /> Add Product
//           </BlueButton>

//           <RefreshButton onClick={fetchProducts} />
//         </div>

//         <CategoryModal
//           isOpen={isOpen}
//           onClose={() => { setIsOpen(false); setSelectedProduct(null); }}
//           onSubmit={handleSubmit}
//           initialData={selectedProduct}
//         />

//         {/* LOADER */}
//         {loading && <RegularLoader />}

//         {/* ERROR */}
//         {error && (
//           <div className="text-center py-4 text-red-500 text-sm">{error}</div>
//         )}

//         {/* TABLE */}
//         {!loading && !error && (
//           <div className="overflow-x-auto">
//             <table className="min-w-full text-sm text-center border border-slate-100 rounded-lg">
//               <thead>
//                 <tr className="border-b border-slate-100 text-slate-500 bg-slate-50">
//                   <th className="py-2">Image</th>
//                   <th className="py-2">Name</th>
//                   <th className="py-2">Categories</th>
//                   <th className="py-2">Validity</th>
//                   <th className="py-2">Claims</th>
//                   <th className="py-2">MRP</th>
//                   <th className="py-2">Status</th>
//                   <th className="py-2">Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {filtered.map((p) => {
//                   const isActive = p.status === 1;
//                   return (
//                     <tr
//                       key={p.id}
//                       className="border-b border-slate-100 bg-white hover:bg-blue-50/60"
//                     >
//                       <td className="py-2">
//                         <img
//                           src={p.image || "/placeholder.png"}
//                           alt={p.name}
//                           className="h-10 w-10 rounded-lg object-cover border"
//                         />
//                       </td>

//                       <td className="py-2 text-slate-700 font-semibold">
//                         {p.name}
//                       </td>

//                       <td className="py-2">
//                         <div className="flex flex-wrap justify-center gap-1">
//                           {p.categories.map((c) => (
//                             <Badge key={c.id} color="blue">
//                               {c.name}
//                             </Badge>
//                           ))}
//                         </div>
//                       </td>

//                       <td className="py-2">{p.validity || "—"}</td>
//                       <td className="py-2">{p.claims || "—"}</td>
//                       <td className="py-2 font-semibold">₹{p.mrp}</td>

//                       <td className="py-2">
//                         <Badge color={isActive ? "green" : "red"}>
//                           {isActive ? "Active" : "Inactive"}
//                         </Badge>
//                       </td>

//                       <td className="py-1.5 text-xs">
//                         <div className="flex items-center justify-center gap-2">

//                           {/* Update */}
//                           <button
//                             onClick={() => handleUpdate(p)}
//                             className="text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center gap-1"
//                           >
//                             <Upload className="w-3 h-3" />
//                             <span>Update</span>
//                           </button>

//                           {/* Toggle Status */}
//                           <button
//                             type="button"
//                             className="text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 flex items-center gap-1"
//                             onClick={() => toggleStatus(p)}
//                           >
//                             <ShieldCheck className="w-3 h-3" />
//                             <span>{isActive ? "Inactive" : "Active"}</span>
//                           </button>
//                         </div>
//                       </td>

//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>

//             {filtered.length === 0 && (
//               <p className="text-center text-xs text-slate-500 py-4">
//                 No products found.
//               </p>
//             )}
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }
